// =============================================================================
// useAcceptanceBasket — T-160 basket-driven UX
//
// One draft hop_submission per project at a time = the "basket". Managers
// toggle photos in/out of it from the HOP gallery. When ready, they
// "Continue to submission" → /projects/:id/hop/submit/new which loads the
// basket photos pre-selected, plus the docs + recipients flow.
//
// Multiple managers on the same project share the basket — once issued,
// it becomes a sent submission and the next toggle starts a fresh draft.
//
// Returns:
//   - basketAssetIds : Set<string>   — photos currently in the basket
//   - draftSubmissionId : string | null
//   - basketSize : number
//   - toggle(assetId) : Promise<void>
//   - clear() : Promise<void>
//   - isLoading : boolean
// =============================================================================

import { useState, useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/lib/auth";

interface BasketState {
  draftSubmissionId: string | null;
  basketAssetIds:    Set<string>;
}

export function useAcceptanceBasket(projectId: string | undefined) {
  const qc       = useQueryClient();
  const userId   = useAuthStore(s => s.user?.user_id ?? null);
  const [busy,   setBusy]   = useState(false);

  // Fetch current draft + its photos
  const { data, isLoading } = useQuery<BasketState>({
    queryKey: ["acceptance_basket", projectId],
    enabled:  !!projectId,
    queryFn: async () => {
      // Find an existing draft submission for this project
      const { data: drafts, error: draftErr } = await supabase
        .from("hop_submissions")
        .select("submission_id")
        .eq("project_id", projectId!)
        .eq("status", "draft")
        .order("created_at", { ascending: false })
        .limit(1);
      if (draftErr) throw draftErr;
      const draftSubmissionId = drafts?.[0]?.submission_id ?? null;

      if (!draftSubmissionId) {
        return { draftSubmissionId: null, basketAssetIds: new Set<string>() };
      }

      // Load the photo asset_ids in this draft
      const { data: rows, error: rowsErr } = await supabase
        .from("hop_submission_photos")
        .select("asset_id")
        .eq("submission_id", draftSubmissionId);
      if (rowsErr) throw rowsErr;
      return {
        draftSubmissionId,
        basketAssetIds: new Set((rows ?? []).map(r => r.asset_id as string)),
      };
    },
    staleTime: 10 * 1000,
  });

  const draftSubmissionId = data?.draftSubmissionId ?? null;
  const basketAssetIds    = data?.basketAssetIds ?? new Set<string>();
  const basketSize        = basketAssetIds.size;

  /**
   * Toggle a photo in/out of the basket. If no draft exists yet, create one.
   * Inserts hop_submission_photos with a default bundle_number=1 + a sequence
   * = (current basket size + 1) so future ordering is stable; the proper
   * bundle layout gets re-set at the submission-build step.
   */
  const toggle = useCallback(async (assetId: string) => {
    if (!projectId || !userId) return;
    setBusy(true);
    try {
      let subId = draftSubmissionId;

      // Lazily create draft on first toggle
      if (!subId) {
        const { data: created, error: createErr } = await supabase
          .from("hop_submissions")
          .insert({
            project_id:        projectId,
            status:            "draft",
            created_by_user_id: userId,
          })
          .select("submission_id")
          .single();
        if (createErr) throw createErr;
        subId = created!.submission_id as string;
      }

      if (basketAssetIds.has(assetId)) {
        // Already in basket → remove
        const { error } = await supabase
          .from("hop_submission_photos")
          .delete()
          .eq("submission_id", subId)
          .eq("asset_id", assetId);
        if (error) throw error;
      } else {
        // Add — bundle_number/sequence are placeholder; the submit-build
        // screen rewrites both at issue time. Use a timestamp-derived
        // sequence to avoid colliding with the unique constraint on
        // (submission_id, bundle_number, sequence_in_bundle) when photos
        // get added/removed/re-added rapidly.
        const seq = Math.floor(Date.now() % 2_000_000_000);  // fits in int4
        const { error } = await supabase
          .from("hop_submission_photos")
          .insert({
            submission_id:      subId,
            asset_id:           assetId,
            bundle_number:      1,
            sequence_in_bundle: seq,
          });
        if (error) throw error;
      }
      await qc.invalidateQueries({ queryKey: ["acceptance_basket", projectId] });
    } finally {
      setBusy(false);
    }
  }, [projectId, userId, draftSubmissionId, basketAssetIds, qc]);

  /**
   * T-279 — Bulk add many photos at once. Filters out any asset_ids already
   * in the basket (no-op for those), then a SINGLE supabase insert for the
   * rest. Designed for "Select all in subcategory" / "Select all on page"
   * UX where N photos go to the basket in one DB round-trip (vs N calls to
   * toggle()). Same draft-creation behaviour as toggle().
   */
  const addMany = useCallback(async (assetIds: string[]) => {
    if (!projectId || !userId) return;
    const toAdd = assetIds.filter(id => !basketAssetIds.has(id));
    if (toAdd.length === 0) return;   // nothing new — short-circuit
    setBusy(true);
    try {
      let subId = draftSubmissionId;

      if (!subId) {
        const { data: created, error: createErr } = await supabase
          .from("hop_submissions")
          .insert({
            project_id:        projectId,
            status:            "draft",
            created_by_user_id: userId,
          })
          .select("submission_id")
          .single();
        if (createErr) throw createErr;
        subId = created!.submission_id as string;
      }

      // Bulk insert — one round-trip for N rows.
      // sequence_in_bundle uses baseSeq + offset to keep uniqueness without
      // colliding with whatever the basket already has (the existing
      // (submission_id, bundle_number, sequence_in_bundle) unique index).
      const baseSeq = Math.floor(Date.now() % 2_000_000_000);
      const rows = toAdd.map((assetId, i) => ({
        submission_id:      subId,
        asset_id:           assetId,
        bundle_number:      1,
        sequence_in_bundle: baseSeq + i,
      }));
      const { error } = await supabase
        .from("hop_submission_photos")
        .insert(rows);
      if (error) throw error;

      await qc.invalidateQueries({ queryKey: ["acceptance_basket", projectId] });
    } finally {
      setBusy(false);
    }
  }, [projectId, userId, draftSubmissionId, basketAssetIds, qc]);

  /** Drop the entire basket — deletes the draft submission row and its children. */
  const clear = useCallback(async () => {
    if (!draftSubmissionId) return;
    setBusy(true);
    try {
      // CASCADE delete handles photos/packs/docs/recipients
      const { error } = await supabase
        .from("hop_submissions")
        .delete()
        .eq("submission_id", draftSubmissionId);
      if (error) throw error;
      await qc.invalidateQueries({ queryKey: ["acceptance_basket", projectId] });
    } finally {
      setBusy(false);
    }
  }, [draftSubmissionId, projectId, qc]);

  return {
    basketAssetIds,
    draftSubmissionId,
    basketSize,
    toggle,
    addMany,
    clear,
    isLoading: isLoading || busy,
  };
}
